import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { isNativeNotWeb } from '@/utils/platform-check';
import { getAppVersionSync } from '@/utils/app-version';

let isRumInitialized = false;
let isLogsInitialized = false;
let isSessionReplayRecording = false;
// null = sampling not yet decided; replay stays off until the backend-controlled
// decision (applyReplaySamplingDecision) lands. true/false = decided for this app run.
let replaySamplingDecision = null;

/**
 * Override console.log to forward logs to Datadog
 */
function setupConsoleLogInterceptor() {
  if (!isLogsInitialized) {
    return;
  }

  const originalConsoleLog = console.log.bind(console);
  const originalConsoleWarn = console.warn.bind(console);
  const originalConsoleError = console.error.bind(console);
  const originalConsoleInfo = console.info.bind(console);
  const originalConsoleDebug = console.debug.bind(console);

  function serializeArgs(args) {
    return args
      .map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      })
      .join(' ');
  }

  console.log = function (...args) {
    originalConsoleLog(...args);
    try {
      datadogLogs.logger.log(serializeArgs(args), {
        level: 'info',
        logger: { name: 'console' },
      });
    } catch (error) {
      originalConsoleError('[Datadog] Failed to forward log:', error);
    }
  };

  console.warn = function (...args) {
    originalConsoleWarn(...args);
    try {
      datadogLogs.logger.warn(serializeArgs(args), {
        logger: { name: 'console' },
      });
    } catch (error) {
      originalConsoleError('[Datadog] Failed to forward warn:', error);
    }
  };

  console.error = function (...args) {
    originalConsoleError(...args);
    try {
      datadogLogs.logger.error(serializeArgs(args), {
        logger: { name: 'console' },
      });
    } catch (error) {
      originalConsoleError('[Datadog] Failed to forward error:', error);
    }
  };

  console.info = function (...args) {
    originalConsoleInfo(...args);
    try {
      datadogLogs.logger.info(serializeArgs(args), {
        logger: { name: 'console' },
      });
    } catch (error) {
      originalConsoleError('[Datadog] Failed to forward info:', error);
    }
  };

  console.debug = function (...args) {
    originalConsoleDebug(...args);
    try {
      datadogLogs.logger.debug(serializeArgs(args), {
        logger: { name: 'console' },
      });
    } catch (error) {
      originalConsoleError('[Datadog] Failed to forward debug:', error);
    }
  };
}

/**
 * Initialize Datadog Logs
 */
export function initDatadogLogs() {
  if (isLogsInitialized) {
    return;
  }

  if (!process.env.VUE_APP_DATADOG_CLIENT_TOKEN) {
    console.warn(
      '[Datadog Logs] Missing VUE_APP_DATADOG_CLIENT_TOKEN. Logs will not be initialized.',
    );
    return;
  }

  try {
    datadogLogs.init({
      clientToken: process.env.VUE_APP_DATADOG_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: process.env.VUE_APP_DATADOG_SERVICE || 'gens-conf',
      env: process.env.VUE_APP_NODE_ENV || 'development',
      forwardErrorsToLogs: true,
      sampleRate: 100,
    });

    isLogsInitialized = true;
    setupConsoleLogInterceptor();
  } catch (error) {
    console.error('[Datadog Logs] Failed to initialize:', error);
  }
}

/**
 * Initialize Datadog RUM
 */
export function initDatadogRum() {
  if (isRumInitialized) {
    return;
  }

  const rumAppId = process.env.VUE_APP_DATADOG_RUM_APP_ID;
  const rumClientToken = process.env.VUE_APP_DATADOG_RUM_CLIENT_TOKEN;

  if (!rumAppId || !rumClientToken) {
    console.warn(
      '[Datadog RUM] Missing VUE_APP_DATADOG_RUM_APP_ID or VUE_APP_DATADOG_RUM_CLIENT_TOKEN. RUM will not be initialized.',
    );
    return;
  }

  try {
    const allowedTracingUrls = [process.env.VUE_APP_API_URL].filter(Boolean);

    datadogRum.init({
      applicationId: rumAppId,
      clientToken: rumClientToken,
      site: 'datadoghq.com',
      service: process.env.VUE_APP_DATADOG_SERVICE || 'gens-conf',
      env: process.env.VUE_APP_NODE_ENV || 'development',
      version: getAppVersionSync(),
      sessionSampleRate: 100,
      // Replay eligibility stays at 100 on purpose. Datadog's sampleRate is
      // static (read once here, can't change at runtime), but we need the rate
      // dialable live from the backend, so we make every session eligible and
      // let applyReplaySamplingDecision() be the single gate via manual start.
      // Do NOT lower this — it would add a second hidden dice roll on top of
      // ours (double-sampling).
      sessionReplaySampleRate: 100,
      startSessionReplayRecordingManually: true,
      sessionPersistence: 'local-storage',
      allowedTracingUrls,
      trackResources: true,
      trackLongTasks: true,
      trackInteractions: true,
      defaultPrivacyLevel: 'mask-user-input',
    });

    isRumInitialized = true;
    // console.log('[Datadog RUM] Initialized. compressIntakeRequests=false, startSessionReplayRecordingManually=true');
  } catch (error) {
    console.error('[Datadog RUM] Failed to initialize:', error);
  }
}

/**
 * Set user context for Datadog RUM
 * @param {Object} userInfo - User information
 * @param {string} userInfo.id - User ID
 * @param {string} [userInfo.name] - User name
 * @param {string} [userInfo.email] - User email
 * @param {Object} [userInfo.additionalInfo] - Additional user properties
 */
export function setDatadogRumUser(userInfo) {
  if (!isRumInitialized) {
    return;
  }

  try {
    datadogRum.setUser({
      id: userInfo.id,
      name: userInfo.name,
      email: userInfo.email,
      ...userInfo.additionalInfo,
    });
  } catch (error) {
    console.error('[Datadog RUM] Failed to set user:', error);
  }
}

/**
 * Clear user context (e.g., on logout)
 */
export function clearDatadogRumUser() {
  if (!isRumInitialized) {
    return;
  }

  try {
    datadogRum.clearUser();
  } catch (error) {
    console.error('[Datadog RUM] Failed to clear user:', error);
  }
}

/**
 * Add custom action/event to RUM
 * @param {string} name - Action name
 * @param {Object} context - Additional context
 */
export function addDatadogRumAction(name, context = {}) {
  if (!isRumInitialized) {
    return;
  }

  try {
    datadogRum.addAction(name, context);
  } catch (error) {
    console.error('[Datadog RUM] Failed to add action:', error);
  }
}

/**
 * Add error to RUM
 * @param {Error} error - Error object
 * @param {Object} context - Additional context
 */
export function addDatadogRumError(error, context = {}) {
  if (!isRumInitialized) {
    return;
  }

  try {
    datadogRum.addError(error, context);
  } catch (e) {
    console.error('[Datadog RUM] Failed to add error:', e);
  }
}

/**
 * Check if RUM is initialized
 */
export function isDatadogRumInitialized() {
  return isRumInitialized;
}

/**
 * Check if Logs is initialized
 */
export function isDatadogLogsInitialized() {
  return isLogsInitialized;
}

/**
 * Get RUM status and diagnostics
 */
export function getDatadogRumStatus() {
  const isNative = isNativeNotWeb();

  if (!isRumInitialized) {
    return {
      initialized: false,
      message: 'RUM not initialized',
      isNative,
    };
  }

  try {
    return {
      initialized: true,
      sessionReplayRecording: isSessionReplayRecording,
      hasDatadogRum: typeof datadogRum !== 'undefined',
      isNative,
      config: {
        applicationId: process.env.VUE_APP_DATADOG_RUM_APP_ID ? 'SET' : 'MISSING',
        clientToken: process.env.VUE_APP_DATADOG_RUM_CLIENT_TOKEN ? 'SET' : 'MISSING',
        site: 'datadoghq.com',
        service: process.env.VUE_APP_DATADOG_SERVICE || 'gens-conf',
        env: process.env.VUE_APP_NODE_ENV || 'development',
        sessionPersistence: 'local-storage',
      },
    };
  } catch (error) {
    return {
      initialized: true,
      error: error.message,
      isNative,
    };
  }
}

/**
 * Decide once per app run whether this session records replay.
 * The rate comes from backend sysparams (DATADOG_REPLAY_SAMPLE_RATE), so cost
 * can be dialed live during an event without shipping an app release.
 *
 * Per-session random sampling: each launch is an independent coin flip, which
 * keeps the recorded subset a representative slice of all usage. We do NOT key
 * this to the user id — that would repeatedly record the same fixed set of
 * people and never the rest.
 *
 * @param {number|string} rate - 0-100 (sysparams sends a string). Missing or
 *   non-numeric fails open to 100, matching the pre-sampling "always record"
 *   behavior so a backend outage never silently kills replay.
 */
export function applyReplaySamplingDecision(rate) {
  if (replaySamplingDecision !== null) {
    return replaySamplingDecision;
  }

  // Explicit missing-value check: Number(null)/Number('') is 0, which would
  // silently disable replay for everyone instead of failing open to 100.
  const parsed = rate === null || rate === undefined || rate === '' ? NaN : Number(rate);
  const normalizedRate = Number.isFinite(parsed) ? Math.min(100, Math.max(0, parsed)) : 100;

  // Extremes are deterministic — no dice roll. 100 always records, 0 never
  // does; only the in-between rates fall to a per-session random draw.
  if (normalizedRate >= 100) {
    replaySamplingDecision = true;
  } else if (normalizedRate <= 0) {
    replaySamplingDecision = false;
  } else {
    replaySamplingDecision = Math.random() * 100 < normalizedRate;
  }

  if (replaySamplingDecision) {
    startSessionReplayRecording();
  }
  return replaySamplingDecision;
}

/**
 * Start session replay recording.
 * Pure primitive: just starts the recording. Sampling is NOT decided here —
 * callers (applyReplaySamplingDecision at boot, handleRouteChangeForSessionReplay
 * on navigation) are responsible for gating on the sampling decision.
 */
export function startSessionReplayRecording() {
  if (!isRumInitialized || isSessionReplayRecording) {
    return;
  }

  try {
    datadogRum.startSessionReplayRecording();
    isSessionReplayRecording = true;
  } catch (error) {
    console.error('[Datadog RUM] Failed to start session replay:', error);
  }
}

/**
 * Stop session replay recording
 */
export function stopSessionReplayRecording() {
  if (!isRumInitialized || !isSessionReplayRecording) {
    return;
  }

  try {
    datadogRum.stopSessionReplayRecording();
    isSessionReplayRecording = false;
  } catch (error) {
    console.error('[Datadog RUM] Failed to stop session replay:', error);
  }
}

/**
 * Handle route change for session replay control
 * Stops recording for sensitive routes, starts for others
 * @param {string} routePath - The route path to check
 */
export function handleRouteChangeForSessionReplay(routePath) {
  if (!isRumInitialized) {
    return;
  }

  // Respect the sampling decision: never (re)start replay for a session that
  // was sampled out, or before the boot-time decision has been made.
  if (replaySamplingDecision !== true) {
    return;
  }

  const sensitiveRoutes = [];
  const isSensitive = sensitiveRoutes.some(r => routePath.startsWith(r));

  if (isSensitive) {
    stopSessionReplayRecording();
  } else {
    startSessionReplayRecording();
  }
}
